import Value from 'customer-data-objects/property/Value';

/**
 * Tiers
 * -----
 */

// Constants
export const VOLUME = 'volume';
export const GRADUATED = 'graduated';
export const STAIRSTEP = 'stairstep';

export const TIERED_PRICING = new Set([GRADUATED, STAIRSTEP, VOLUME]);

export const MIN_TIERS_LENGTH = 2;
export const MAX_TIER_PRICE = 2147483647;

// Types
export type Tier = {
  start: number;
  end?: number;
  price?: number;
};

export type TierRange = {
  start: number;
  end?: number;
};

export type TierPrice = {
  index: number;
  price?: number;
  currency?: string;
};

// Type Guards
export function isTier(value: unknown): value is Tier {
  return (
    !!value &&
    typeof value === 'object' &&
    Object.prototype.hasOwnProperty.call(value, 'start')
  );
}

export function isTierArray(value: unknown): value is Tier[] {
  return Array.isArray(value) && (value.length === 0 || isTier(value.at(0)));
}

export function isTierRange(value: unknown): value is TierRange {
  return (
    !!value &&
    typeof value === 'object' &&
    Object.prototype.hasOwnProperty.call(value, 'start')
  );
}

export function isTierRangeArray(value: unknown): value is TierRange[] {
  return (
    Array.isArray(value) && (value.length === 0 || isTierRange(value.at(0)))
  );
}

export function isTierPrice(value: unknown): value is TierPrice {
  return (
    !!value &&
    typeof value === 'object' &&
    Object.prototype.hasOwnProperty.call(value, 'index')
  );
}

export function isTierPriceArray(value: unknown): value is TierPrice[] {
  return (
    Array.isArray(value) && (value.length === 0 || isTierPrice(value.at(0)))
  );
}

// Type Assertions
export function assertIsTier(value: unknown): asserts value is Tier {
  if (!isTier(value)) {
    throw new Error(`${value} is not a Tier`);
  }
}

export function assertIsTierArray(value: unknown): asserts value is Tier[] {
  if (!isTierArray(value)) {
    throw new Error(`${value} is not a Tier[]`);
  }
}

export function assertIsTierRangeArray(
  value: unknown
): asserts value is TierRange[] {
  if (!isTierRangeArray(value)) {
    throw new Error(`${value} is not a TierRange[]`);
  }
}

export function assertIsTierPriceArray(
  value: unknown
): asserts value is TierPrice[] {
  if (!isTierPriceArray(value)) {
    throw new Error(`${value} is not a TierPrice[]`);
  }
}

/**
 * Pricing Model
 * ------------
 */

// Constants
export const FLAT = 'flat';
export const TIERED = 'tiered';

// Types
export type PricingModelCategory = typeof FLAT | typeof TIERED;

export type PricingModel =
  | typeof GRADUATED
  | typeof STAIRSTEP
  | typeof VOLUME
  | typeof FLAT;

export type TieredPricingModel = Exclude<PricingModel, typeof FLAT>;

// Type Guards
export function isPricingModelTiered(
  value: Value
): value is TieredPricingModel {
  if (!value) return false;
  return TIERED_PRICING.has(value);
}

export function isPricingModelFlat(value: any): value is typeof FLAT {
  return value === FLAT || !value;
}

export function isPricingModel(value: Value): value is PricingModel {
  return isPricingModelTiered(value) || isPricingModelFlat(value);
}

// Type Assertions
export function assertIsTieredPricingModel(
  value: Value
): asserts value is TieredPricingModel {
  if (!isPricingModelTiered(value)) {
    throw new Error(`${value} is not a PricingMethodValue`);
  }
}

export function assertIsPricingModel(
  value: Value
): asserts value is PricingModel {
  if (!isPricingModel(value)) {
    throw new Error(`${value} is not a PricingModel`);
  }
}
